import React, { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { toast } from 'react-toastify';

import moment from 'moment';

import { useAuth } from 'context/auth';
import { ValidationGroup } from 'devextreme-react';
import DataSource from 'devextreme/data/data_source';
import { RequestItemPath, RequestItemPathValue } from 'pages/RequestTask/Task.entity';
import $createDataSource from 'utils/create-data-souce-instance';
import { isSuccess } from 'utils/responseStatus';

import { useGetMethodOfReceptionTypesQuery } from 'bus/methodOfReception/services';
import { MethodOfReception } from 'bus/methodOfReception/types';
import { useGetProfileInfoQuery } from 'bus/profile/services/services';
import {
  useChangeRequestMutation,
  useLazyGetPricesQuery,
  useLazyGetRequestQuery,
  useLazyRequestPumpPriceQuery,
} from 'bus/request';
import { RequestItem, RequestPriceResponse } from 'bus/request/types';

import { calcPumpPrice, calcPumpPriceMinimal } from './TaskDetail.utils';
import { DetailMainInfo } from './View';
import { initialForm } from './View/DetailMainInfo/utils';

type Props = {
  storeContragent: DataSource;
  storeCustomer: DataSource;
  storeContractorLegal: DataSource;
};

export const TaskDetailScreen: FC<Props> = memo(({ storeContragent, storeCustomer, storeContractorLegal }) => {
  let { id } = useParams<{ id: string }>();
  const validateRef = useRef<ValidationGroup>(null);

  const { getClaims }: any = useAuth();
  const { data: profileInfoQuery } = useGetProfileInfoQuery();
  const { data: methodTypes } = useGetMethodOfReceptionTypesQuery();
  //const { data: dataRequestInfo, isLoading, refetch, isUninitialized } = useGetRequestQuery({ id });

  const [getRequest, { isLoading }] = useLazyGetRequestQuery();

  const [getPricesQuery, { data: pricesDataList }] = useLazyGetPricesQuery();
  const [changeRequest, { isLoading: isLoadingChangeRequest }] = useChangeRequestMutation();

  const [requestPumpPrice, { data: pumpPrice }] = useLazyRequestPumpPriceQuery();

  const isCanSelectFactoryRequest: boolean = getClaims(['CanSelectFactoryRequest']);

  const [form, setForm] = useState<RequestItem>(initialForm());

  console.log('form', form);

  const handleRequestPumpPrice = useCallback(
    (currentForm: RequestItem) => {
      if (
        !currentForm.date ||
        !currentForm.factoryId ||
        !currentForm.objectId ||
        !currentForm.methodOfReception.transportId
      ) {
        return;
      }

      requestPumpPrice({
        date: moment(currentForm.date).format('YYYY-MM-DD'),
        factoryId: currentForm.factoryId,
        objectId: currentForm.objectId,
        transportId: currentForm.methodOfReception.transportId,
      })
        .unwrap()
        .then((data) => {
          if (isSuccess(data)) {
            const currentPumpMoving = () => {
              if (currentForm.methodOfReception.pumpMovingCount == 0) {
                return 0;
              }

              return currentForm.methodOfReception.pumpMovingFilledManually
                ? currentForm.methodOfReception.pumpMoving
                : data.pumpMoving;
            };

            const currentPumpPipes = () => {
              if (currentForm.methodOfReception.pumpPipesCount == 0) {
                return 0;
              }

              return currentForm.methodOfReception.pumpPipesFilledManually
                ? currentForm.methodOfReception.pumpPipes
                : data.pumpPipes;
            };

            const currentDowntime = () => {
              if (currentForm.methodOfReception.downtimeHours == 0) {
                return 0;
              }

              return currentForm.methodOfReception.downtimeFilledManually
                ? currentForm.methodOfReception.downtime
                : data.downtime;
            };

            const currentPumpQuencher = () => {
              if (!currentForm.methodOfReception.useQuencher) {
                return 0;
              }

              return currentForm.methodOfReception.pumpQuencherFilledManually
                ? currentForm.methodOfReception.pumpQuencher
                : data.pumpQuencher;
            };

            setForm((prev) => ({
              ...prev,
              methodOfReception: {
                ...prev.methodOfReception,
                pumpPriceMinimal: calcPumpPriceMinimal(prev.methodOfReception, data),
                pumpPrice: calcPumpPrice(prev.methodOfReception, data),
                pumpInnings: currentForm.methodOfReception.pumpInningsFilledManually
                  ? currentForm.methodOfReception.pumpInnings
                  : data.pumpInnings,
                pumpQuencher: currentPumpQuencher(),
                pumpMoving: currentPumpMoving(),
                pumpPipes: currentPumpPipes(),
                downtime: currentDowntime(),
              },
            }));
          }
        });
    },
    [requestPumpPrice],
  );

  // useEffect(() => {
  //   if (dataRequestInfo && !isUninitialized) {
  //     handleRequestPumpPrice({ ...dataRequestInfo });
  //   }
  // }, [dataRequestInfo, handleRequestPumpPrice, isUninitialized]);

  const handleChangeForm = useCallback(
    <T extends RequestItemPath>(name: T, newValue: RequestItemPathValue<T>) => {
      setForm((prev) => {
        let updated;
        if (name.includes('.')) {
          const [objectKey, propertyKey] = name.split('.') as [keyof RequestItem, keyof MethodOfReception];
          updated = {
            ...prev,
            [objectKey]: {
              ...(prev[objectKey] as MethodOfReception),
              [propertyKey]: newValue,
            },
          };

          // Дополнительная логика для pumpQuantity и isFilledManually
          if (
            objectKey === 'methodOfReception' &&
            (propertyKey === 'pumpQuantity' || propertyKey === 'isFilledManually')
          ) {
            updated = {
              ...updated,
              methodOfReception: {
                ...updated.methodOfReception,
                pumpPriceMinimal: updated.methodOfReception.pumpPrice
                  ? calcPumpPriceMinimal(prev[objectKey], updated.methodOfReception.pumpPrice)
                  : 0,
                pumpPrice: updated.methodOfReception.pumpPrice
                  ? calcPumpPrice(prev[objectKey], updated.methodOfReception.pumpPrice)
                  : 0,
              },
            };
          }
        } else {
          updated = {
            ...prev,
            [name]: newValue,
          };
        }

        if (
          name === 'date' ||
          name === 'factoryId' ||
          name === 'objectId' ||
          name === 'methodOfReception.transportId'
        ) {
          handleRequestPumpPrice(updated);
        }

        return updated;
      });
    },
    [handleRequestPumpPrice],
  );

  const handleGetNeedPrice = useCallback(
    (form: RequestItem, init: boolean = false) => {
      const factoryId = isCanSelectFactoryRequest ? form.factoryId : profileInfoQuery?.factoryId;
      const dateFormat = moment(form.date).format('YYYY-MM-DD');
      if (form.contractorId && form.date && factoryId && form.nomenclatureId) {
        getPricesQuery({
          contractorId: form.contractorId,
          date: dateFormat,
          factoryId,
          nomenclatureId: form.nomenclatureId,
          density: form.density,
          objectId: form.objectId,
        })
          .unwrap()
          .then((data) => {
            if (init) {
              return;
            }
            for (let item of data) {
              if (item.paymentMethod === form.paymentMethod) {
                const price = form.priceFilledManually ? form.price : item.price;
                let delivery = form.deliveryFilledManually ? form.delivery : item.deliveryPrice;

                if (form.deliveryFilledManually) {
                  delivery = form.delivery;
                } else {
                  delivery = item.deliveryMaxQuantity >= form.quantity ? item.deliveryPrice : 0;
                }

                setForm((prev) => ({ ...prev, price, delivery }));
              }
            }
          });
      }
    },
    [getPricesQuery, isCanSelectFactoryRequest, profileInfoQuery?.factoryId],
  );

  const handleChangeFormGroup = useCallback((newValues: RequestItem) => {
    setForm((prev) => ({ ...prev, ...newValues }));
  }, []);

  const nomenclatureDataSource = useMemo(() => {
    return $createDataSource('/v1/Nomenclature', 'id', { defaultSort: [{ selector: 'comment', desc: false }] });
  }, []);

  const handleGetRequest = useCallback(() => {
    getRequest({
      id,
    }).then((data) => {
      if (isSuccess(data)) {
        if (data.data) {
          setForm(data.data);
          handleGetNeedPrice({ ...data.data }, true);
          handleRequestPumpPrice({ ...data.data });
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleGetRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = useCallback(
    async (e: any) => {
      e.preventDefault && e.preventDefault();
      try {
        if (validateRef.current?.instance.validate().isValid) {
          const dateFormat = moment(form.date).format('YYYY-MM-DDTHH:mm:ss');

          await changeRequest({ ...form, date: dateFormat, id: Number(id) })
            .unwrap()
            .then(() => {
              toast.success('Изменения успешно сохранены', {
                position: toast.POSITION.BOTTOM_RIGHT,
              });
              //refetch();
            });
        }
      } catch {}
    },
    [changeRequest, form, id],
  );

  const clearButton = (name: keyof RequestPriceResponse) => {
    const old = pumpPrice?.[name];

    setForm((prev) => ({ ...prev, methodOfReception: { ...prev.methodOfReception, [name]: old } }));
  };

  const clearButtonPrice = () => {
    if (!pricesDataList) return;

    for (let item of pricesDataList) {
      if (item.paymentMethod === form.paymentMethod) {
        setForm((prev) => ({ ...prev, price: item.price, priceFilledManually: false }));
      }
    }
  };

  const clearButtonDelivery = () => {
    if (!pricesDataList) return;

    for (let item of pricesDataList) {
      if (item.paymentMethod === form.paymentMethod) {
        const delivery = item.deliveryMaxQuantity >= form.quantity ? item.deliveryPrice : 0;

        setForm((prev) => ({ ...prev, delivery, deliveryFilledManually: false }));
      }
    }
  };

  if (!profileInfoQuery) {
    return <div>Загрузка...</div>;
  }

  return (
    <DetailMainInfo
      nomenclatureDataSource={nomenclatureDataSource}
      storeCustomer={storeCustomer}
      methodTypes={methodTypes}
      isLoading={isLoading}
      form={form}
      onChangeForm={handleChangeForm}
      onSubmit={handleSubmit}
      isLoadingSubmit={isLoadingChangeRequest}
      ref={validateRef}
      onChangeFormGroup={handleChangeFormGroup}
      onGetNeedPrice={handleGetNeedPrice}
      pricesDataList={pricesDataList}
      storeContragent={storeContragent}
      storeContractorLegal={storeContractorLegal}
      clearButton={clearButton}
      clearButtonPrice={clearButtonPrice}
      clearButtonDelivery={clearButtonDelivery}
      pumpPrice={pumpPrice}
    />
  );
});
